import { useTranslation } from "react-i18next";
import { cn } from "~/foundation/utils/cn";

/** The sections of a thinker's dialog; `?panel=` deep-links into one. */
export type ThinkerPanel = "model" | "facets" | "hive" | "try";
export const THINKER_PANELS: readonly ThinkerPanel[] = ["model", "facets", "hive", "try"];
export const parseThinkerPanel = (value: unknown): ThinkerPanel | null =>
  typeof value === "string" && (THINKER_PANELS as readonly string[]).includes(value)
    ? (value as ThinkerPanel)
    : null;
export const panelId = (panel: ThinkerPanel) => `thinker-panel-${panel}`;
export const tabId = (panel: ThinkerPanel) => `thinker-tab-${panel}`;

/**
 * The tabs under a thinker dialog's title (2026-10-10, owner: "once a facet is selected the setup
 * is unusable"): Model · Facets · Hive · Try it. Sticky at the top of the scrolling body, and a
 * horizontal strip on a phone. Before the thinker exists only Model is open — facets, the hive
 * and a try all need a stored row.
 */
export function ThinkerTabs({
  value,
  onChange,
  saved,
}: {
  value: ThinkerPanel;
  onChange: (panel: ThinkerPanel) => void;
  /** The thinker has a stored row: the other tabs have something to work on. */
  saved: boolean;
}) {
  const { t } = useTranslation();
  const labels: Record<ThinkerPanel, string> = {
    model: t("Model"),
    facets: t("Facets"),
    hive: t("Hive"),
    try: t("Try it"),
  };
  return (
    <div className="sticky top-0 z-10 flex flex-col gap-1 bg-menu-bg pb-2">
      <div
        role="tablist"
        aria-label={t("Thinker sections")}
        className="flex gap-1 overflow-x-auto border-b border-divider"
      >
        {THINKER_PANELS.map((panel) => {
          const disabled = panel !== "model" && !saved;
          const selected = value === panel;
          return (
            <button
              key={panel}
              type="button"
              role="tab"
              id={tabId(panel)}
              aria-selected={selected}
              aria-controls={panelId(panel)}
              aria-disabled={disabled || undefined}
              disabled={disabled}
              title={disabled ? t("Save the thinker first") : undefined}
              className={cn(
                "shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-small",
                selected
                  ? "border-text font-medium text-text"
                  : "border-transparent text-text-secondary hover:text-text",
                disabled && "cursor-not-allowed opacity-50",
              )}
              onClick={() => {
                if (!disabled) onChange(panel);
              }}
            >
              {labels[panel]}
            </button>
          );
        })}
      </div>
      {!saved ? (
        <p className="text-small text-text-tertiary">
          {t("Save the thinker first — facets, the hive and a try need a stored thinker.")}
        </p>
      ) : null}
    </div>
  );
}
